
import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useRoyaltyReports } from '@/hooks/useRoyaltyReports';
import { useNavigate } from 'react-router-dom';
import { useAuthors } from '@/hooks/useAuthors';
import { useRoyaltyReportsData } from '@/hooks/useRoyaltyReportsData';
import { useDeleteRoyaltyReport } from '@/hooks/useDeleteRoyaltyReport';
import RoyaltyReportsFilters from '@/components/admin/royalties/RoyaltyReportsFilters';
import RoyaltyReportsTable from '@/components/admin/royalties/RoyaltyReportsTable';
import EditReportDialog from '@/components/admin/EditReportDialog';
import DeleteReportDialog from '@/components/admin/DeleteReportDialog';

interface GroupedReport {
  authorId: string;
  year: number;
  quarter: number;
  reports: any[];
  totalSales: number;
  totalAmount: number;
  status: string;
  countries: Set<string>;
}

const AdminRoyalties: React.FC = () => {
  const { data: royaltyReports, isLoading } = useRoyaltyReports();
  const { data: authors } = useAuthors();
  const deleteReportMutation = useDeleteRoyaltyReport();
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [authorFilter, setAuthorFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReportGroup, setSelectedReportGroup] = useState<GroupedReport | null>(null);
  const navigate = useNavigate();

  const { years, statuses, countries, groupedReports } = useRoyaltyReportsData(royaltyReports || []);

  // Apply filters to grouped reports
  const filteredReports = React.useMemo(() => {
    return groupedReports.filter(group => {
      if (yearFilter !== 'all' && group.year !== parseInt(yearFilter)) {
        return false;
      }
      
      if (statusFilter !== 'all' && group.status !== statusFilter) {
        return false;
      }
      
      if (authorFilter !== 'all' && group.authorId !== authorFilter) {
        return false;
      }
      
      if (countryFilter !== 'all') {
        const hasCountry = Array.from(group.countries).includes(countryFilter);
        if (!hasCountry) return false;
      }
      
      return true;
    });
  }, [groupedReports, yearFilter, statusFilter, authorFilter, countryFilter]);

  // Handle view button click - navigate to the first report in the group
  const handleViewReport = (group: GroupedReport) => {
    const firstReport = group.reports[0];
    navigate(`/admin/royalties/${firstReport.id}`);
  };

  // Handle edit button click - open edit dialog
  const handleEditReport = (group: GroupedReport) => {
    setSelectedReportGroup(group);
    setEditDialogOpen(true);
  };

  // Handle delete button click - open delete dialog
  const handleDeleteReport = (group: GroupedReport) => {
    setSelectedReportGroup(group);
    setDeleteDialogOpen(true);
  };

  // Handle confirm delete - delete all reports in the group
  const handleConfirmDelete = async () => {
    if (!selectedReportGroup) return;

    try {
      // Delete all reports in the group
      for (const report of selectedReportGroup.reports) {
        await deleteReportMutation.mutateAsync(report.id);
      }
      setDeleteDialogOpen(false);
      setSelectedReportGroup(null);
    } catch (error) {
      console.error('Error deleting reports:', error);
    }
  };

  return (
    <MainLayout allowedRoles={['admin']}>
      <PageHeader
        title="Royalty Reports"
        description="Manage all author royalty reports"
      />
      
      <div className="mb-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle>All Reports</CardTitle>
              
              <RoyaltyReportsFilters
                yearFilter={yearFilter}
                statusFilter={statusFilter}
                authorFilter={authorFilter}
                countryFilter={countryFilter}
                onYearFilterChange={setYearFilter}
                onStatusFilterChange={setStatusFilter}
                onAuthorFilterChange={setAuthorFilter}
                onCountryFilterChange={setCountryFilter}
                years={years}
                statuses={statuses}
                countries={countries}
                authors={authors}
              />
            </div>
          </CardHeader>
          
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center p-8">
                <p>Loading reports...</p>
              </div>
            ) : filteredReports.length > 0 ? (
              <RoyaltyReportsTable
                filteredReports={filteredReports}
                authors={authors}
                onViewReport={handleViewReport}
                onEditReport={handleEditReport}
                onDeleteReport={handleDeleteReport}
              />
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No royalty reports found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <EditReportDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        reportGroup={selectedReportGroup}
      />

      <DeleteReportDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        reportGroup={selectedReportGroup}
        onConfirmDelete={handleConfirmDelete}
        isDeleting={deleteReportMutation.isPending}
      />
    </MainLayout>
  );
};

export default AdminRoyalties;
